import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Bell, CalendarClock, CheckCheck, ClipboardCheck, X } from 'lucide-react'

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@components/ui/popover'
import { ScrollArea } from '@components/ui/scroll-area'
import { cn } from '@lib/utils'
import { useNotifications, type AppNotification } from '@hooks/useNotifications'

const typeIcon: Record<AppNotification['type'], React.ComponentType<{ className?: string }>> = {
  appointment_approval: ClipboardCheck,
  appointment_upcoming: CalendarClock,
}

export function NotificationBell() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const { notifications, unreadCount, markRead, markAllRead, dismiss } = useNotifications()

  const handleOpen = (n: AppNotification) => {
    markRead(n.id)
    setOpen(false)
    navigate(n.href)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="p-2 text-ml-on-surface-variant hover:text-ml-primary transition-colors relative min-h-0"
          aria-label={t('notifications.title')}
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-ml-error text-[10px] font-bold leading-none text-white"
              aria-hidden="true"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-[360px] p-0 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold">{t('notifications.title')}</span>
            {unreadCount > 0 && (
              <span className="text-[11px] text-ml-on-surface-variant">
                {unreadCount} {t('notifications.unread')}
              </span>
            )}
          </div>
          {unreadCount > 0 && (
            <button
              type="button"
              onClick={markAllRead}
              className="inline-flex items-center gap-1 text-[11px] font-medium text-ml-primary hover:underline"
            >
              <CheckCheck className="h-3.5 w-3.5" />
              {t('notifications.mark_all_read')}
            </button>
          )}
        </div>

        {notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
            <Bell className="h-8 w-8 text-ml-on-surface-variant/40" />
            <p className="text-sm text-ml-on-surface-variant">{t('notifications.no_new')}</p>
          </div>
        ) : (
          <ScrollArea className="max-h-[380px]">
            <ul className="divide-y">
              {notifications.map((n) => {
                const Icon = typeIcon[n.type]
                return (
                  <li key={n.id} className="group relative">
                    <button
                      type="button"
                      onClick={() => handleOpen(n)}
                      className={cn(
                        'w-full flex items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-ml-surface-container-high',
                        !n.read && 'bg-ml-primary/5'
                      )}
                    >
                      <span
                        className={cn(
                          'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
                          n.type === 'appointment_approval'
                            ? 'bg-ml-error/10 text-ml-error'
                            : 'bg-ml-primary/10 text-ml-primary'
                        )}
                      >
                        <Icon className="h-4 w-4" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-2">
                          <span className="text-sm font-medium truncate">{n.title}</span>
                          {!n.read && (
                            <span className="h-2 w-2 shrink-0 rounded-full bg-ml-primary" aria-hidden="true" />
                          )}
                        </span>
                        {n.description && (
                          <span className="mt-0.5 block text-xs text-ml-on-surface-variant line-clamp-2">
                            {n.description}
                          </span>
                        )}
                        {n.timeLabel && (
                          <span className="mt-1 block text-[11px] text-ml-on-surface-variant/70">
                            {n.timeLabel}
                          </span>
                        )}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        dismiss(n.id)
                      }}
                      aria-label={t('common.dismiss', 'Descartar')}
                      className="absolute top-2 right-2 hidden rounded p-1 text-ml-on-surface-variant/60 hover:text-ml-primary group-hover:block"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </li>
                )
              })}
            </ul>
          </ScrollArea>
        )}
      </PopoverContent>
    </Popover>
  )
}
